import {createSelector} from "@reduxjs/toolkit";

import {APPRECIATION_DEFAULTS, registerAbteilungIIAppreciationKeySuffix} from "@reva/shared/appreciations";
import {withLiveLeaseholdEntries} from "@reva/shared/register";

import {selectArg} from "../util/selectors";
import {getAppraisalValueDetails} from "../util/valueIndicationUtil";
import {selectEnergyAppreciationText} from "./energySelectors";
import {selectDescriptionData} from "./locationInfoSlice";
import {selectAllDocProperties, selectSingleDocProperty, selectSingleRegisterParam} from "./objectParametersSlice";
import {updateParamAndValidateAndSubmitFormThunk} from "./objectParametersThunk";
import {selectReadOnly} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import type {PlainRevaSelector, RevaRootState} from "./store";

/** Registered appreciations whose default text depends on other object parameters. */
interface AppreciationVariant {
    /** suffix appended to the appreciation key to pick its default text */
    computeSuffix?: (state: RevaRootState) => string;
    /** computes the full default text directly (no JSON default lookup) */
    computeText?: (state: RevaRootState) => string | null;
    /** shown in the override warning; an AVM output-parameter key or a plain display string */
    variantLabel: string;
}

const APPRECIATION_VARIANTS: Record<string, AppreciationVariant> = {
    appreciation_object_energy: {
        computeText: selectEnergyAppreciationText,
        variantLabel: "energy_efficiency",
    },
    appreciation_object_register_abteilung_ii: {
        // Zählt auch eine nur live aus der Indikation abgeleitete Erbbau-/Vorkaufsrecht-Zeile mit,
        // die (noch) nicht persistiert ist (#19379).
        computeSuffix: (state) =>
            registerAbteilungIIAppreciationKeySuffix(
                withLiveLeaseholdEntries(
                    selectSingleRegisterParam(state, "register_abt2"),
                    getAppraisalValueDetails(state.valueIndication?.indicationData),
                ).length > 0,
            ),
        variantLabel: "Tabelle Abteilung II",
    },
};

export const APPRECIATION_KEYS_WITH_VARIANTS: string[] = Object.keys(APPRECIATION_VARIANTS);

/** @return the override-warning label for a variant appreciation key */
export const selectAppreciationVariantLabel = (appreciationKey: string): string | undefined =>
    APPRECIATION_VARIANTS[appreciationKey]?.variantLabel;

/** @return the currently derived default text of a variant appreciation key */
const selectAppreciationVariantText = (state: RevaRootState, appreciationKey: string): string | null => {
    const variant = APPRECIATION_VARIANTS[appreciationKey];
    if (!variant) return null;
    if (variant.computeText) return variant.computeText(state);
    const suffix = variant.computeSuffix?.(state) ?? "";
    return APPRECIATION_DEFAULTS[appreciationKey + suffix]?.text ?? null;
};

/** @return every variant appreciation key mapped to its currently derived default text */
export const selectAllAppreciationVariantTexts = (state: RevaRootState): Record<string, string | null> =>
    Object.fromEntries(APPRECIATION_KEYS_WITH_VARIANTS.map((key) => [key, selectAppreciationVariantText(state, key)]));

/** @return pdf_title of every variant appreciation currently overridden with a manually entered text */
export const selectOverriddenVariantAppreciationTitles = createSelector([selectAllDocProperties], (docProperties) =>
    APPRECIATION_KEYS_WITH_VARIANTS.filter((key) => typeof docProperties[key] === "string" && docProperties[key]).map(
        (key) => APPRECIATION_DEFAULTS[key]?.pdf_title ?? key,
    ),
);

/** Generic appreciation key for a position in the GUI structure. */
export interface AppreciationKeyObj {
    /** page position for the appreciation */
    page: string;
    tab?: string;
    section?: string;
}

export type AppreciationKey = AppreciationKeyObj | string;

const APPRECIATION_PREFIX = "appreciation_";

/**
 * Serialize and normalize an AppreciationKey to string.
 * @param key the key to serialize
 * @param ignoreInvalid if true, don't throw an error if invalid appreciation key is provided
 * @return serialized String for storage in persistence or lookup in default values.
 */
export const serializeKey = (key: AppreciationKey | null | undefined, ignoreInvalid?: boolean): string => {
    if (typeof key === "string") {
        if (key.startsWith(APPRECIATION_PREFIX)) return key;
        return APPRECIATION_PREFIX + key;
    }
    if (key && typeof key === "object" && typeof key.page === "string") {
        let result = APPRECIATION_PREFIX + key.page;
        if (key.tab) result += `_${key.tab.replaceAll(/[ -]/g, "_")}`;
        if (key.section) result += `_${key.section.replaceAll(/[ -]/g, "_")}`;
        return result;
    }
    if (ignoreInvalid) return "";
    console.error("Invalid appreciation key: ", key);
    throw new Error(`Invalid appreciation key: ${key}`);
};

export const getAppreciationId = (key: AppreciationKey | null | undefined): string => {
    return serializeKey(key, true).replace(/^appreciation_/, "");
};

/**
 * Curried appreciation selector: supply the key to obtain a plain selector. Takes the same key union as
 * {@link serializeKey} and shares its contract - an unusable key throws, it is a programming error.
 */
export type CurriedAppreciationSelector<R> = (
    appreciationKey: AppreciationKey | null | undefined,
) => PlainRevaSelector<R>;

/**
 * Curry a selector function which has an appreciation key as only parameter. The selector may declare
 * further parameters as long as they are optional, as the single-parameter slice selectors do.
 */
const currySelectorWithAppreciationKey =
    <R>(
        selector: (state: RevaRootState, appreciationKey: string, ...rest: never[]) => R,
    ): CurriedAppreciationSelector<R> =>
    (appreciationKey) => {
        const serializedKey = serializeKey(appreciationKey);
        return (state) => selector(state, serializedKey);
    };

/** Selector function for an appreciation value or its default text (or null). */
const selectAppreciationValueOrDefault = createSelector(
    [selectArg<string>, selectSingleDocProperty, selectDescriptionData, selectAppreciationVariantText],
    (appreciationKey, docPropertyValue, descriptionData, variantText): string | null => {
        if (typeof docPropertyValue === "string" && docPropertyValue) {
            return docPropertyValue;
        }
        if (appreciationKey === serializeKey({page: "location", tab: "micro"}) && descriptionData?.micro_text) {
            return descriptionData?.micro_text;
        }
        if (appreciationKey === serializeKey({page: "location", tab: "macro"}) && descriptionData?.macro_text) {
            return descriptionData?.macro_text;
        }
        if (APPRECIATION_KEYS_WITH_VARIANTS.includes(appreciationKey)) {
            return variantText;
        }
        return APPRECIATION_DEFAULTS[appreciationKey]?.["text"] ?? null;
    },
);

/** Curried selector for the displayed appreciation value. */
export const selectAppreciationValueOrDefaultCurried: CurriedAppreciationSelector<string | null> =
    currySelectorWithAppreciationKey(selectAppreciationValueOrDefault);

/** Selector function for whether a specific appreciation has a stored value (custom, not default). */
const selectAppreciationHasStoredValue = createSelector([selectSingleDocProperty], (docPropertyValue): boolean => {
    return typeof docPropertyValue === "string" && !!docPropertyValue;
});

/** Curried selector for whether a specific appreciation has a stored value (custom, not default). */
export const selectAppreciationHasStoredValueCurried: CurriedAppreciationSelector<boolean> =
    currySelectorWithAppreciationKey(selectAppreciationHasStoredValue);

/** Selector function for whether a specific appreciation has any value (stored or default). */
const selectAppreciationHasValue = createSelector([selectAppreciationValueOrDefault], (valueOrDefault): boolean => {
    return typeof valueOrDefault === "string" && !!valueOrDefault;
});

/** Curried selector for whether a specific appreciation has a stored value. */
export const selectAppreciationHasValueCurried: CurriedAppreciationSelector<boolean> =
    currySelectorWithAppreciationKey(selectAppreciationHasValue);

/**
 * Selector function for whether a specific appreciation button should be visible
 * - either because the valuation is not yet finalized, or because the valuation has a custom stored value.
 */
const selectAppreciationIsVisible = createSelector(
    [selectAppreciationHasStoredValue, selectReadOnly],
    (appreciationHasStoredValue, readOnly): boolean => {
        return appreciationHasStoredValue || !readOnly;
    },
);

/** Curried selector for whether a specific appreciation button should be visible. */
export const selectAppreciationIsVisibleCurried: CurriedAppreciationSelector<boolean> =
    currySelectorWithAppreciationKey(selectAppreciationIsVisible);

/**
 * Async thunk to update an appreciation value.
 * @param appreciationKey appreciation key to update; same contract as {@link serializeKey}
 * @param newValue updated value
 */
export const changeAppreciationValueAsync =
    (appreciationKey: AppreciationKey | null | undefined, newValue: string | null): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const propertyName = serializeKey(appreciationKey);
        const readOnly = selectReadOnly(getState());
        if (readOnly) {
            console.warn(
                "Could not change an appreciation text while the valuation is read-only (snapshot or finalized)!",
            );
            return;
        }
        await dispatch(updateParamAndValidateAndSubmitFormThunk(propertyName, "DOC_PROPERTY", newValue, null));
    };
